import request from './request'

/**
 * 会议管理 API（会议跟踪模块）
 */
export const meetingApi = {
  /**
   * 分页查询会议列表（我相关的）
   * @param {Object} params - { page, size, keyword, meetingType, status, customerId, startTimeFrom, startTimeTo }
   * @returns {Promise<{records: Array, total: number, current: number, size: number, pages: number}>}
   */
  getMeetingList(params) {
    return request({ url: '/meetings', method: 'get', params })
  },
  /**
   * 会议详情（含参会人、决策项进度、纪要最新版本号）
   * @param {number} id 会议ID
   */
  getMeetingDetail(id) {
    return request({ url: `/meetings/${id}`, method: 'get' })
  },
  /**
   * 创建会议
   * @param {Object} data - { title, meetingType, meetingMode, startTime, endTime, location, onlineLink, customerId, remark, attendeeUserIds, reminderBefore }
   *   meetingMode: 'OFFLINE'|'ONLINE'|'HYBRID'（必填）；OFFLINE 必填 location，ONLINE 必填 onlineLink，HYBRID 两者必填
   */
  createMeeting(data) {
    return request({ url: '/meetings', method: 'post', data })
  },
  /**
   * 更新会议（null 字段不更新；传空串可覆盖清空，如切换 meetingMode 后清理地点/线上链接）
   * @param {number} id 会议ID
   * @param {Object} data 同创建（不含 id；meetingMode、onlineLink 语义同创建）
   */
  updateMeeting(id, data) {
    return request({ url: `/meetings/${id}`, method: 'put', data })
  },
  /** 取消会议 @param {number} id */
  cancelMeeting(id) {
    return request({ url: `/meetings/${id}/cancel`, method: 'put' })
  },
  /** 结束会议 @param {number} id */
  endMeeting(id) {
    return request({ url: `/meetings/${id}/end`, method: 'put' })
  },
  /** 删除会议 @param {number} id */
  deleteMeeting(id) {
    return request({ url: `/meetings/${id}`, method: 'delete' })
  },

  /** 我的会议模板列表 @returns {Promise<Array>} */
  getMeetingTemplateList() {
    return request({ url: '/meeting-templates', method: 'get' })
  },
  /**
   * 保存会议模板（id 为空新建，否则更新）
   * @param {Object} data - { id?, name, meetingType, meetingMode, location, onlineLink, durationMinutes, attendeeUserIds }
   */
  saveMeetingTemplate(data) {
    return request({ url: '/meeting-templates', method: 'post', data })
  },
  /** 删除会议模板 @param {number} id */
  deleteMeetingTemplate(id) {
    return request({ url: `/meeting-templates/${id}`, method: 'delete' })
  },

  /**
   * 添加参会人（NORMAL 正式进日程收邀请；TEMP 临时不进日程直接已接受；仅待召开会议）
   * @param {number} meetingId 会议ID
   * @param {Object} data - { userIds: number[], attendType: 'NORMAL'|'TEMP' }
   */
  addAttendees(meetingId, data) {
    return request({ url: `/meetings/${meetingId}/attendees`, method: 'post', data })
  },
  /** 移除参会人（仅待召开会议） @param {number} meetingId @param {number} userId */
  removeAttendee(meetingId, userId) {
    return request({ url: `/meetings/${meetingId}/attendees/${userId}`, method: 'delete' })
  },
  /**
   * 标记出勤（仅已结束会议）
   * @param {number} meetingId 会议ID
   * @param {number} userId 参会人用户ID
   * @param {Object} data - { attendance: 'NONE'|'PRESENT'|'ABSENT' }
   */
  markAttendance(meetingId, userId, data) {
    return request({ url: `/meetings/${meetingId}/attendees/${userId}/attendance`, method: 'put', data })
  },

  /**
   * 分页查询决策项
   * @param {Object} params - { page, size, meetingId, assigneeId, status, keyword }
   */
  getActionItemList(params) {
    return request({ url: '/action-items', method: 'get', params })
  },
  /**
   * 我的决策项（服务端注入当前用户）
   * @param {Object} params - { page, size, status, keyword }
   */
  getMyActionItemList(params) {
    return request({ url: '/action-items/mine', method: 'get', params })
  },
  /**
   * 创建决策项（自动生成待办）
   * @param {number} meetingId 会议ID
   * @param {Object} data - { title, content, assigneeId, deadline }
   */
  createActionItem(meetingId, data) {
    return request({ url: `/meetings/${meetingId}/actions`, method: 'post', data })
  },
  /**
   * 更新决策项（负责人变更会重建待办）
   * @param {number} id 决策项ID
   * @param {Object} data - { title, content, assigneeId, deadline }
   */
  updateActionItem(id, data) {
    return request({ url: `/action-items/${id}`, method: 'put', data })
  },
  /**
   * 决策项状态流转
   * @param {number} id 决策项ID
   * @param {Object} data - { status: 'PENDING'|'IN_PROGRESS'|'DONE'|'CANCELLED', progressNote? }
   */
  updateActionItemStatus(id, data) {
    return request({ url: `/action-items/${id}/status`, method: 'put', data })
  },
  /**
   * 更新决策项进展
   * @param {number} id 决策项ID
   * @param {Object} data - { progressNote }
   */
  updateActionItemProgress(id, data) {
    return request({ url: `/action-items/${id}/progress`, method: 'put', data })
  },
  /** 删除决策项 @param {number} id */
  deleteActionItem(id) {
    return request({ url: `/action-items/${id}`, method: 'delete' })
  },
  /**
   * 催办决策项（仅组织者/主持人，每日一次幂等）
   * @param {number} id 决策项ID
   */
  urgeActionItem(id) {
    return request({ url: `/action-items/${id}/urge`, method: 'post' })
  },

  /** 我的决策项统计 @returns {Promise<Object>} { total, pending, inProgress, done, cancelled, overdue, doneRate, dailyTrend } */
  getMyActionStats() {
    return request({ url: '/meetings/stats/mine', method: 'get' })
  },
  /** 我管理的会议统计 @returns {Promise<Object>} { meetingCount, actionTotal, actionDone, overdueCount, doneRate, meetings, assignees } */
  getManagedMeetingStats() {
    return request({ url: '/meetings/stats/managed', method: 'get' })
  },

  /**
   * 会议物料列表
   * @param {number} meetingId 会议ID
   */
  getMaterialList(meetingId) {
    return request({ url: `/meetings/${meetingId}/materials`, method: 'get' })
  },
  /**
   * 绑定已上传文件为会议物料
   * @param {number} meetingId 会议ID
   * @param {Object} data - { fileName, ossKey, url, fileSize, contentType }
   */
  bindMaterial(meetingId, data) {
    return request({ url: `/meetings/${meetingId}/materials`, method: 'post', data })
  },
  /** 删除物料 @param {number} meetingId @param {number} id */
  deleteMaterial(meetingId, id) {
    return request({ url: `/meetings/${meetingId}/materials/${id}`, method: 'delete' })
  },
  /**
   * 获取物料临时访问 URL（预签名，限时有效）
   * @param {number} meetingId 会议ID
   * @param {number} id 物料ID
   * @returns {Promise<string>} 预签名URL
   */
  getMaterialAccessUrl(meetingId, id) {
    return request({ url: `/meetings/${meetingId}/materials/${id}/url`, method: 'get' })
  },

  /**
   * 保存会议纪要（新增版本）
   * @param {number} meetingId 会议ID
   * @param {Object} data - { content }
   */
  saveMinutes(meetingId, data) {
    return request({ url: `/meetings/${meetingId}/minutes`, method: 'post', data })
  },
  /** 最新纪要 @param {number} meetingId */
  getLatestMinutes(meetingId) {
    return request({ url: `/meetings/${meetingId}/minutes/latest`, method: 'get' })
  },
  /** 纪要版本列表 @param {number} meetingId */
  getMinutesVersionList(meetingId) {
    return request({ url: `/meetings/${meetingId}/minutes/versions`, method: 'get' })
  },
  /** 指定版本纪要 @param {number} meetingId @param {number} version */
  getMinutesVersion(meetingId, version) {
    return request({ url: `/meetings/${meetingId}/minutes/versions/${version}`, method: 'get' })
  },
  /**
   * 上传纪要文件（生成 FILE 类型新版本，成功后通知全体参会人）
   * @param {number} meetingId 会议ID
   * @param {Object} data - { fileName, ossKey, url, fileSize, contentType }
   */
  uploadMinutesFile(meetingId, data) {
    return request({ url: `/meetings/${meetingId}/minutes/file`, method: 'post', data })
  },
  /**
   * 获取 FILE 版本纪要文件的临时访问 URL（预签名，限时有效）
   * @param {number} meetingId 会议ID
   * @param {number} version 版本号
   * @returns {Promise<string>} 预签名URL
   */
  getMinutesFileAccessUrl(meetingId, version) {
    return request({ url: `/meetings/${meetingId}/minutes/versions/${version}/url`, method: 'get' })
  }
}

export default meetingApi
